import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaImages, FaInfoCircle, FaEnvelope, FaPlay, FaCompactDisc, FaRecordVinyl, FaHeadphones } from 'react-icons/fa';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import API_BASE_URL from '../config';
import SEO from './SEO';
import './HomePage.css';

const HomePage = () => {
  const { t } = useLanguage();
  const [imageError, setImageError] = useState(false);
  const [currentImage, setCurrentImage] = useState(null);

  // Загружаем главное фото
  useEffect(() => {
    const fetchMainImage = async () => {
      try {
        // Сначала пытаемся загрузить установленное главное фото
        const mainImageResponse = await axios.get(`${API_BASE_URL}/api/homepage/main-image`);
        if (mainImageResponse.data && mainImageResponse.data.url) {
          const imageUrl = `${API_BASE_URL}${mainImageResponse.data.url}`;
          console.log('✅ Загружено главное фото:', imageUrl);
          setCurrentImage(imageUrl);
        } else {
          // Если главное фото не установлено, используем дефолтное
          console.log('ℹ️ Главное фото не установлено, используем дефолтное');
          setCurrentImage("/images/main-photo.jpg");
        }
      } catch (error) {
        console.error('❌ Ошибка загрузки главного фото:', error);
        // В случае ошибки используем дефолтное фото
        setCurrentImage("/images/main-photo.jpg");
      }
    };
    fetchMainImage();
  }, []);

  // Определяем, какое изображение использовать: приоритет - фото из галереи
  const imageSrc = currentImage || "/images/main-photo.jpg";

  return (
    <>
      <SEO 
        title="Home"
        description="Sergey Levitskiy - Professional DJ and Music Producer. Commercial and Underground Electronic Music. Book for events, weddings, and special occasions."
        keywords="Sergey Levitskiy, DJ, Music Producer, Electronic Music, Commercial DJ, Underground Music, Event DJ, Wedding DJ"
      />
      <div className="home-page">
      <motion.div
        className="home-content"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        {/* Hero Section */}
        <motion.section
          className="hero-section"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.8 }}
        >
          <motion.div 
            className="hero-image-wrapper"
            initial={{ opacity: 0, scale: 0.8, rotate: -5 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ delay: 0.4, duration: 1, type: "spring", stiffness: 100 }}
            whileHover={{ scale: 1.05, rotate: 2 }}
          >
            {/* Decorative elements */}
            <div className="photo-decoration photo-decoration-1"></div>
            <div className="photo-decoration photo-decoration-2"></div>
            <div className="photo-decoration photo-decoration-3"></div>
            
            {/* Glitch effect overlay */}
            <div className="glitch-overlay"></div>
            
            {/* Photo frame */}
            <div className="photo-frame">
              {!imageError ? (
                <motion.img 
                  key={imageSrc}
                  src={imageSrc} 
                  alt="Sergey Levitskiy" 
                  className="hero-main-image"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5 }}
                  onLoad={() => {
                    console.log('✅ Фото успешно загружено:', imageSrc);
                  }}
                  onError={(e) => {
                    console.error('❌ Ошибка загрузки фото:', imageSrc, e);
                    // Если фото не загрузилось, показываем placeholder
                    setImageError(true);
                  }}
                />
              ) : (
                <div className="photo-placeholder">
                  <div className="placeholder-icon">
                  </div>
                  <div className="placeholder-text">
                    <span className="placeholder-name">SL</span>
                  </div>
                  {currentImage && (
                    <button 
                      className="try-fallback-btn"
                      onClick={() => {
                        setImageError(false);
                        // Пытаемся загрузить изображение снова
                        const img = new Image();
                        img.onload = () => {
                          setImageError(false);
                        };
                        img.onerror = () => {
                          setImageError(true);
                        };
                        img.src = currentImage;
                      }}
                    >
                      Retry
                    </button>
                  )}
                </div>
              )}
              {/* Scanline effect */}
              <div className="scanline"></div>
            </div>
            
            {/* Floating particles */}
            <div className="particles">
              {[...Array(20)].map((_, i) => (
                <motion.div
                  key={i}
                  className="particle"
                  initial={{ 
                    x: Math.random() * 100 + '%',
                    y: Math.random() * 100 + '%',
                    opacity: 0
                  }}
                  animate={{
                    y: [null, Math.random() * -100 - 50 + 'px'],
                    opacity: [0, 0.6, 0],
                    scale: [0, 1, 0]
                  }}
                  transition={{
                    duration: Math.random() * 3 + 2,
                    repeat: Infinity,
                    delay: Math.random() * 2,
                    ease: "easeOut"
                  }}
                />
              ))}
            </div>
          </motion.div>
          <div className="hero-text-content">
            <motion.h1
              className="hero-main-title"
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
            >
              {t('home.title')}
            </motion.h1>
            <motion.p
              className="hero-subtitle"
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6, duration: 0.8 }}
            >
              {t('home.subtitle')}
            </motion.p>
            <motion.p
              className="hero-description"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.8 }}
            >
              {t('home.description')}
            </motion.p>
            <motion.div
              className="hero-cta"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.8 }}
            >
              <Link to="/player" className="cta-button primary">
                <FaPlay /> {t('home.listenNow')}
              </Link>
              <Link to="/about" className="cta-button secondary">
                <FaInfoCircle /> {t('home.learnMore')}
              </Link>
            </motion.div>
          </div>
        </motion.section>

        {/* Projects Section */}
        <motion.section
          className="projects-section"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.8 }}
        >
          <h2 className="section-title">{t('home.projects')}</h2>
          <div className="projects-grid">
            <motion.div
              className="project-card"
              whileHover={{ scale: 1.05, y: -5 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <div className="project-icon commercial">
                <FaRecordVinyl />
              </div>
              <h3>{t('home.commercial')}</h3>
              <p>{t('home.commercialDesc')}</p>
              <Link to="/commercial" className="project-link">
                {t('home.explore')} →
              </Link>
            </motion.div>
            <motion.div
              className="project-card"
              whileHover={{ scale: 1.05, y: -5 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <div className="project-icon underground">
                <FaHeadphones />
              </div>
              <h3>{t('home.underground')}</h3>
              <p>{t('home.undergroundDesc')}</p>
              <Link to="/underground" className="project-link">
                {t('home.explore')} →
              </Link>
            </motion.div>
          </div>
        </motion.section>

        {/* Quick Links */}
        <motion.section
          className="quick-links-section"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4, duration: 0.8 }}
        >
          <h2 className="section-title">{t('home.quickLinks')}</h2>
          <div className="quick-links-grid">
            <Link to="/player" className="quick-link-card">
              <FaCompactDisc className="link-icon" />
              <h3>{t('nav.music')}</h3>
              <p>{t('home.musicDesc')}</p>
            </Link>
            <Link to="/gallery" className="quick-link-card">
              <FaImages className="link-icon" />
              <h3>{t('nav.gallery')}</h3>
              <p>{t('home.galleryDesc')}</p>
            </Link>
            <Link to="/about" className="quick-link-card">
              <FaInfoCircle className="link-icon" />
              <h3>{t('nav.about')}</h3>
              <p>{t('home.learnMore')}</p>
            </Link>
            <Link to="/contacts" className="quick-link-card">
              <FaEnvelope className="link-icon" />
              <h3>{t('nav.contacts')}</h3>
              <p>{t('contacts.title')}</p>
            </Link>
          </div>
        </motion.section>
      </motion.div>
    </div>
    </>
  );
};

export default HomePage;

